import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreenButton } from "@fortawesome/pro-solid-svg-icons/faMobileScreenButton";
import { useEffect, useRef, useState } from "react";
import { Button, Card, CardContent } from "@wondermaps/ui";

const DISMISSED_KEY = "pwa-install-dismissed";
const DELAY_MS = 60_000; // 60 seconds

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallPrompt() {
  const { t } = useTranslation("common");
  const deferredPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const mountTimeRef = useRef(Date.now());
  const [visible, setVisible] = useState(false);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    // Already dismissed
    if (localStorage.getItem(DISMISSED_KEY)) {
      return;
    }

    // Already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return;
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e as BeforeInstallPromptEvent;
      // Trigger re-render to check elapsed time
      forceUpdate((n) => n + 1);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Check periodically if 60s has elapsed
    const interval = setInterval(() => {
      forceUpdate((n) => n + 1);
    }, 5000);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      clearInterval(interval);
    };
  }, []);

  // Determine visibility on each render
  useEffect(() => {
    const elapsed = Date.now() - mountTimeRef.current;
    const dismissed = localStorage.getItem(DISMISSED_KEY);
    const installed = window.matchMedia("(display-mode: standalone)").matches;
    const hasPrompt = deferredPromptRef.current !== null;

    setVisible(hasPrompt && elapsed >= DELAY_MS && !dismissed && !installed);
  });

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setVisible(false);
  };

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;

    if (outcome === "accepted") {
      deferredPromptRef.current = null;
    }
    setVisible(false);
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-0 right-0 z-50 p-4">
      <Card className="mx-auto max-w-md">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2">
              <FontAwesomeIcon icon={faMobileScreenButton} className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-foreground">{t("install.addToHomeScreen")}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("install.description")}
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={handleInstall}>
                  {t("add")}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDismiss}>
                  {t("notNow")}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
